import { useEffect, useRef, useState } from "react";
import { CloseButton, Icon, Input, InputGroup, type InputGroupProps } from "@chakra-ui/react";
import { LuSearch } from "react-icons/lu";

/**
 * The search box above a filtered list.
 *
 * Typing is never held up by the list. What is typed shows at once; the list
 * is filtered only once typing pauses, so a long table is not re-filtered (and
 * the address rewritten) on every key. Clearing is immediate, with its own
 * button inside the field, as in Chakra's "clear button" example.
 *
 * `value` is the applied search, usually from the address. When it changes from
 * outside -- "Clear filters", Back -- the field follows it.
 */
export function SearchInput({
  value,
  onChange,
  placeholder,
  label,
  delay = 300,
  ...groupProps
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  /** For screen readers, where the placeholder is not enough. */
  label?: string;
  delay?: number;
} & Omit<InputGroupProps, "children" | "onChange">) {
  const [draft, setDraft] = useState(value);
  const applied = useRef(value);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Changed from outside: follow it.
  useEffect(() => {
    if (value !== applied.current) {
      applied.current = value;
      setDraft(value);
    }
  }, [value]);

  // Changed here: apply it once typing pauses.
  useEffect(() => {
    if (draft === applied.current) {
      return;
    }
    const timer = setTimeout(() => {
      applied.current = draft;
      onChangeRef.current(draft);
    }, delay);
    return () => clearTimeout(timer);
  }, [draft, delay]);

  const clear = () => {
    setDraft("");
    applied.current = "";
    onChangeRef.current("");
    inputRef.current?.focus();
  };

  return (
    <InputGroup
      startElement={<Icon as={LuSearch} boxSize={4} color="fg.muted" />}
      endElement={draft === "" ? undefined : (
        <CloseButton size="xs" me="-2" aria-label="Clear search" onClick={clear} />
      )}
      {...groupProps}
    >
      <Input
        ref={inputRef}
        size="sm"
        placeholder={placeholder}
        aria-label={label ?? placeholder}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
      />
    </InputGroup>
  );
}
